import { useState, useEffect, useCallback, useRef } from "react";
import {
  listCargoEntries, createCargoEntry, updateCargoEntry, deleteCargoEntry,
  uploadCargoExcel, bulkCargoStatus, listCargoServices, setEntryServices,
  getCargoFlightAssignments, listCargoFlights, splitEntryAcrossFlights,
} from "@/lib/api";
import { useRefreshOnFocus } from "@/hooks/useRefreshOnFocus";
import { FIELD_GROUPS, STATUS_STYLES, STATUSES, DIRECTIONS, emptyEntry } from "@/lib/cargoFields";
import { CalendarRange, Search, Plus, Upload, Pencil, Trash2, X, PackageCheck, Receipt, ChevronRight, ChevronLeft, ArrowDownUp, Split, Star } from "lucide-react";
import { DirectionBadge } from "@/components/DirectionBadge";
import { CargoEntryField, fmtCargoVal, withMrnRule } from "@/components/CargoEntryField";

const FAV_CAT = "__favourites__";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const Field = CargoEntryField;
const fmtVal = fmtCargoVal;

export default function CargoGlobalPlanningPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("delivered");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyEntry());
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const [allServices, setAllServices] = useState([]);
  const [svcEntry, setSvcEntry] = useState(null);
  const [picked, setPicked] = useState({});
  const [savingSvc, setSavingSvc] = useState(false);
  const [svcSearch, setSvcSearch] = useState("");
  const [svcCat, setSvcCat] = useState(null);
  const [svcParty, setSvcParty] = useState("cargo_agency");
  const [byEntry, setByEntry] = useState({});
  const [sortMode, setSortMode] = useState("newest"); // "newest" (default) | "oldest"
  const [splitEntry, setSplitEntry] = useState(null);
  const [allFlights, setAllFlights] = useState([]);
  useEffect(() => { listCargoFlights().then((r) => setAllFlights(r.data || [])).catch(() => {}); }, []);

  useEffect(() => { listCargoServices(new Date().getFullYear(), true).then((r) => setAllServices((r.data || []).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999)))).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, asg] = await Promise.all([
        listCargoEntries({
          direction: direction || undefined, status: status || undefined,
          date_from: dateFrom || undefined, date_to: dateTo || undefined, q: q || undefined,
        }),
        getCargoFlightAssignments().catch(() => ({ data: { by_entry: {} } })),
      ]);
      setByEntry(asg.data?.by_entry || {});
      setEntries(res.data || []);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  }, [direction, status, dateFrom, dateTo, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useRefreshOnFocus(load);

  // Expand split AWBs (spread across >1 flight) into one display line per flight leg.
  const buildLines = () => {
    const lines = [];
    for (const e of entries) {
      const allocs = byEntry[e.id] || [];
      if (allocs.length > 1) {
        allocs.forEach((a, i) => lines.push({
          ...e,
          _key: `${e.id}__${a.flight_id || i}`,
          _date: a.flight_date || e.shipment_date || "",
          _split: true,
          _leg: a,
          _entryRef: e,
          flight_no: a.flight_number || e.flight_no,
          pieces: a.pieces ?? e.pieces,
          weight_kg: a.weight_kg ?? e.weight_kg,
        }));
      } else {
        lines.push({ ...e, _key: e.id, _date: e.shipment_date || "", _split: false, _entryRef: e });
      }
    }
    const today = new Date().toISOString().slice(0, 10);
    const cmp = (a, b) => {
      const da = a._date || "", db = b._date || "";
      if (sortMode === "oldest") {
        return da.localeCompare(db) || String(a.shipment_id || "").localeCompare(String(b.shipment_id || ""));
      }
      // newest-first: most recent dates at the top
      return db.localeCompare(da) || String(a.shipment_id || "").localeCompare(String(b.shipment_id || ""));
    };
    return lines.sort(cmp);
  };
  const displayLines = buildLines();

  const setField = (k, v) => setForm((p) => withMrnRule(p, k, v));
  const openNew = () => { setForm(emptyEntry()); setEditId(null); setDialogOpen(true); };
  const openEdit = (e) => { setForm({ ...emptyEntry(), ...e }); setEditId(e.id); setDialogOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editId) { await updateCargoEntry(editId, form); toast.success("Entry updated"); }
      else { await createCargoEntry(form); toast.success("Entry created"); }
      setDialogOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await deleteCargoEntry(deleteId); toast.success("Entry deleted"); setDeleteId(null); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await uploadCargoExcel(file, "append");
      toast.success(`Imported ${res.data.imported} cargo entries`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected((prev) => prev.size === entries.length ? new Set() : new Set(entries.map((e) => e.id)));

  const applyBulk = async () => {
    try {
      const res = await bulkCargoStatus([...selected], bulkStatus);
      toast.success(`Updated ${res.data.modified} entries to ${bulkStatus}`);
      load();
    } catch { toast.error("Bulk update failed"); }
  };

  const openSvc = (e) => {
    setSvcEntry(e);
    setSvcCat(null);
    setSvcParty("cargo_agency");
    const init = {};
    (e.services || []).forEach((s) => {
      init[s.service_id] = { qty: s.qty, unit_price: s.unit_price, service: allServices.find((a) => a.id === s.service_id) || { id: s.service_id, name: s.name, unit: s.unit, price: s.unit_price } };
    });
    setPicked(init);
    setSvcSearch("");
  };
  const toggleSvc = (svc, entry) => setPicked((prev) => {
    const n = { ...prev };
    if (n[svc.id]) delete n[svc.id];
    else {
      const u = (svc.unit || "").toLowerCase();
      let qty = 1;
      if (u.includes("kg")) qty = Number(entry.weight_kg) || 1;
      else if (u.includes("piece") || u.includes("pcs")) qty = Number(entry.pieces) || 1;
      n[svc.id] = { qty, unit_price: svc.price === "" ? 0 : Number(svc.price), service: svc };
    }
    return n;
  });
  const setSvcField = (id, k, v) => setPicked((prev) => ({ ...prev, [id]: { ...prev[id], [k]: v } }));
  const pickedArr = Object.values(picked);
  const svcTotal = pickedArr.reduce((s, v) => s + Number(v.qty || 0) * Number(v.unit_price || 0), 0);
  const partyOf = (v) => (v.service?.party || "cargo_agency");
  const svcTotalAirline = pickedArr.filter((v) => partyOf(v) === "airline").reduce((s, v) => s + Number(v.qty || 0) * Number(v.unit_price || 0), 0);
  const svcTotalAgency = pickedArr.filter((v) => partyOf(v) === "cargo_agency").reduce((s, v) => s + Number(v.qty || 0) * Number(v.unit_price || 0), 0);
  const saveSvc = async () => {
    setSavingSvc(true);
    try {
      const arr = Object.entries(picked).map(([sid, v]) => ({
        service_id: sid, name: v.service?.name || "", unit: v.service?.unit || "",
        party: v.service?.party || "cargo_agency",
        qty: Number(v.qty || 0), unit_price: Number(v.unit_price || 0),
        amount: Number(v.qty || 0) * Number(v.unit_price || 0),
      }));
      await setEntryServices(svcEntry.id, arr);
      toast.success(`Services saved for ${svcEntry.shipment_id || "entry"}`);
      setSvcEntry(null); load();
    } catch { toast.error("Failed to save services"); }
    finally { setSavingSvc(false); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="cargo-planning-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarRange className="text-emerald-500" /> Global Cargo Planning
          </h1>
          <p className="text-slate-500 text-sm mt-1">All shipments, one line per AWB (split AWBs show one line per flight). Click a row for full details.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <Button variant="outline" onClick={() => setSortMode((m) => (m === "newest" ? "oldest" : "newest"))} data-testid="planning-sort-toggle" title="Toggle date sort order" className="flex-1 sm:flex-none">
            <ArrowDownUp size={16} className="mr-2" /> {sortMode === "newest" ? "Newest → Oldest" : "Oldest → Newest"}
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onImportFile} className="hidden" data-testid="cargo-import-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="cargo-import-btn" className="flex-1 sm:flex-none">
            <Upload size={18} className="mr-2" /> {importing ? "Importing…" : "Import Excel"}
          </Button>
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 sm:flex-none" data-testid="cargo-new-entry-btn">
            <Plus size={18} className="mr-2" /> New Entry
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div>
          <Label className="text-xs text-slate-500">Direction</Label>
          <select value={direction} onChange={(e) => setDirection(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="filter-direction">
            <option value="">All</option>
            {DIRECTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">Status</Label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="filter-status">
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-500">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" data-testid="filter-date-from" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" data-testid="filter-date-to" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">Search</Label>
          <div className="relative mt-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="AWB, MRN, airline…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="filter-search" />
          </div>
        </div>
      </div>

      {/* Bulk toolbar */}
      {selected.size > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex flex-wrap items-center gap-3" data-testid="bulk-toolbar">
          <span className="text-sm font-medium text-emerald-800">{selected.size} selected</span>
          <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="bulk-status-select">
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <Button size="sm" onClick={applyBulk} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="bulk-apply-btn">
            <PackageCheck size={16} className="mr-1" /> Set status
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 bg-slate-50 border-b border-slate-200">
                <th className="py-3 px-3"><input type="checkbox" checked={entries.length > 0 && selected.size === entries.length} onChange={toggleAll} className="w-4 h-4 accent-emerald-600" data-testid="select-all" /></th>
                <th className="py-3 px-4">Date</th>
                <th className="py-3 px-4">Airline</th>
                <th className="py-3 px-4">Flight</th>
                <th className="py-3 px-4">AWB</th>
                <th className="py-3 px-4">Dir</th>
                <th className="py-3 px-4">Route</th>
                <th className="py-3 px-4">Pcs</th>
                <th className="py-3 px-4">Kg</th>
                <th className="py-3 px-4">Description</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="planning-tbody">
              {loading ? (
                <tr><td colSpan={12} className="py-8 text-center text-slate-400">Loading…</td></tr>
              ) : displayLines.length === 0 ? (
                <tr><td colSpan={12} className="py-8 text-center text-slate-400">No shipments match these filters.</td></tr>
              ) : displayLines.map((e) => (
                <tr key={e._key} className="border-b border-slate-100 hover:bg-emerald-50/40 cursor-pointer" onClick={() => setDetail(e)} data-testid={`planning-row-${e._key}`}>
                  <td className="py-2.5 px-3" onClick={(ev) => ev.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggleSel(e.id)} className="w-4 h-4 accent-emerald-600" data-testid={`select-${e._key}`} />
                  </td>
                  <td className="py-2.5 px-4 whitespace-nowrap">{e._date || "—"}</td>
                  <td className="py-2.5 px-4">{e.airline || "—"}</td>
                  <td className="py-2.5 px-4">{e.flight_no || "—"}</td>
                  <td className="py-2.5 px-4 font-mono">
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      {e.shipment_id || "—"}
                      {e._split && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5" title="This AWB is split across multiple flights">
                          <Split size={10} /> Split
                        </span>
                      )}
                      {e._split && (byEntry[e.id] || []).length > 1 && (
                        <span className="text-[10px] font-sans text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5" title="Pieces/weight per flight leg" data-testid={`leg-totals-${e.id}`}>
                          {(byEntry[e.id] || []).map((a) => `${a.pieces}/${a.weight_kg}`).join(" · ")}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="py-2.5 px-4"><DirectionBadge dir={e.direction} /></td>
                  <td className="py-2.5 px-4">{[e.origin, e.destination].filter(Boolean).join(" → ") || "—"}</td>
                  <td className="py-2.5 px-4">{e.pieces || "—"}</td>
                  <td className="py-2.5 px-4">{e.weight_kg || "—"}</td>
                  <td className="py-2.5 px-4 max-w-[200px] truncate">{e.description || "—"}</td>
                  <td className="py-2.5 px-4"><span className={`px-2 py-0.5 rounded border text-xs capitalize ${STATUS_STYLES[e.status] || ""}`}>{e.status}</span></td>
                  <td className="py-2.5 px-4" onClick={(ev) => ev.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => setSplitEntry(e._entryRef || e)} className="h-8 w-8 text-slate-500 hover:text-amber-600" data-testid={`split-entry-${e._key}`} title="Split this AWB across flights">
                        <Split size={16} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => openSvc(e)} className="h-8 w-8 text-slate-500 hover:text-emerald-600 relative" data-testid={`services-entry-${e._key}`} title="Choose services for invoicing">
                        <Receipt size={16} />
                        {e.services?.length > 0 && <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{e.services.length}</span>}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(e)} className="h-8 w-8 text-slate-500 hover:text-emerald-600" data-testid={`edit-entry-${e._key}`}><Pencil size={16} /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(e.id)} className="h-8 w-8 text-slate-500 hover:text-red-600" data-testid={`delete-entry-${e._key}`}><Trash2 size={16} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards — shipments */}
      <div className="md:hidden space-y-2" data-testid="planning-cards">
        {loading ? (
          <p className="py-8 text-center text-slate-400 text-sm">Loading…</p>
        ) : displayLines.length === 0 ? (
          <p className="py-8 text-center text-slate-400 text-sm">No shipments match these filters.</p>
        ) : displayLines.map((e) => (
          <div key={`${e._key}-mc`} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid={`planning-card-${e._key}`}>
            <div className="flex items-start justify-between gap-2">
              <button onClick={() => setDetail(e)} className="min-w-0 text-left" data-testid={`planning-card-open-${e._key}`}>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="font-mono font-semibold text-slate-900">{e.shipment_id || "—"}</span>
                  {e._split && <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5"><Split size={10} /> Split</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{e.airline || "—"} · {e.flight_no || "—"} · {e._date || "—"}</p>
              </button>
              <label onClick={(ev) => ev.stopPropagation()} className="shrink-0">
                <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggleSel(e.id)} className="w-4 h-4 accent-emerald-600 mt-1" data-testid={`select-${e._key}-m`} />
              </label>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <div><span className="text-slate-400">Route </span><span className="text-slate-700">{[e.origin, e.destination].filter(Boolean).join(" → ") || "—"}</span></div>
              <div className="flex items-center gap-1"><span className="text-slate-400">Dir</span> <DirectionBadge dir={e.direction} /></div>
              <div><span className="text-slate-400">Pcs </span><span className="text-slate-700">{e.pieces || "—"}</span></div>
              <div><span className="text-slate-400">Kg </span><span className="text-slate-700">{e.weight_kg || "—"}</span></div>
            </div>
            {e.description && <p className="mt-1.5 text-xs text-slate-500 truncate">{e.description}</p>}
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className={`px-2 py-0.5 rounded border text-xs capitalize ${STATUS_STYLES[e.status] || ""}`}>{e.status}</span>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" onClick={() => setSplitEntry(e._entryRef || e)} className="h-8 w-8 text-slate-500 hover:text-amber-600" data-testid={`split-entry-${e._key}-m`}><Split size={16} /></Button>
                <Button size="icon" variant="ghost" onClick={() => openSvc(e)} className="h-8 w-8 text-slate-500 hover:text-emerald-600 relative" data-testid={`services-entry-${e._key}-m`}><Receipt size={16} />{e.services?.length > 0 && <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{e.services.length}</span>}</Button>
                <Button size="icon" variant="ghost" onClick={() => openEdit(e)} className="h-8 w-8 text-slate-500 hover:text-emerald-600" data-testid={`edit-entry-${e._key}-m`}><Pencil size={16} /></Button>
                <Button size="icon" variant="ghost" onClick={() => setDeleteId(e.id)} className="h-8 w-8 text-slate-500 hover:text-red-600" data-testid={`delete-entry-${e._key}-m`}><Trash2 size={16} /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Details modal */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cargo-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="font-mono">{detail?.shipment_id}</span>
              {detail && <span className={`px-2 py-0.5 rounded border text-xs capitalize ${STATUS_STYLES[detail.status] || ""}`}>{detail.status}</span>}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-5 py-2">
              {FIELD_GROUPS.map((g) => (
                <div key={g.title}>
                  <p className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-2">{g.title}</p>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                    {g.fields.map((f) => (
                      <div key={f.key} className="text-sm">
                        <span className="text-slate-400">{f.label}: </span>
                        <span className="text-slate-800 font-medium">{fmtVal(f, detail[f.key])}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { const e = detail; setDetail(null); openEdit(e); }} data-testid="detail-edit-btn"><Pencil size={16} className="mr-1" /> Edit</Button>
            <Button onClick={() => setDetail(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="cargo-entry-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Cargo Entry" : "New Cargo Entry"}</DialogTitle></DialogHeader>
          <div className="space-y-6 py-2">
            {FIELD_GROUPS.map((group) => (
              <div key={group.title}>
                <p className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-2">{group.title}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {group.fields.map((f) => <Field key={f.key} field={f} value={form[f.key]} onChange={setField} />)}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="cargo-cancel-btn"><X size={16} className="mr-1" /> Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cargo-save-btn">{saving ? "Saving…" : editId ? "Update Entry" : "Create Entry"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Services picker for invoicing */}
      <Dialog open={!!svcEntry} onOpenChange={(o) => !o && setSvcEntry(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cargo-services-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Receipt size={18} className="text-emerald-500" /> Services for <span className="font-mono">{svcEntry?.shipment_id}</span>{svcCat ? <span className="text-slate-400 font-normal text-sm">· {svcCat === FAV_CAT ? "Favourites" : svcCat}</span> : null}</DialogTitle>
          </DialogHeader>

          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1 self-start" data-testid="svc-party-tabs">
            {[{ key: "cargo_agency", label: "Cargo Agency" }, { key: "airline", label: "Airline" }].map((p) => {
              const cnt = Object.values(picked).filter((v) => (v.service?.party || "cargo_agency") === p.key).length;
              return (
                <button key={p.key} type="button" onClick={() => { setSvcParty(p.key); setSvcCat(null); setSvcSearch(""); }}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${svcParty === p.key ? (p.key === "airline" ? "bg-blue-600 text-white shadow-sm" : "bg-emerald-600 text-white shadow-sm") : "text-slate-600 hover:text-slate-900"}`}
                  data-testid={`svc-party-tab-${p.key}`}>
                  {p.label}{cnt ? <span className="ml-1 opacity-80">({cnt})</span> : null}
                </button>
              );
            })}
          </div>

          {!svcCat ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="svc-category-list">
              {(() => {
                const partyServices = allServices.filter((s) => (s.party || "cargo_agency") === svcParty);
                const byCat = {};
                partyServices.forEach((s) => { const c = s.category || "Other"; (byCat[c] = byCat[c] || []).push(s); });
                const selByCat = {};
                Object.values(picked).filter((v) => (v.service?.party || "cargo_agency") === svcParty).forEach((v) => { const c = v.service?.category || "Other"; selByCat[c] = (selByCat[c] || 0) + 1; });
                const favs = partyServices.filter((s) => s.is_favorite);
                const cats = favs.length ? [FAV_CAT, ...Object.keys(byCat)] : Object.keys(byCat);
                if (cats.length === 0) {
                  return <p className="col-span-full text-center text-slate-400 text-sm py-6">No {svcParty === "airline" ? "Airline" : "Cargo Agency"} services yet. Add them under Services &amp; Rates.</p>;
                }
                const countFor = (c) => (c === FAV_CAT ? favs.length : byCat[c].length);
                const selFor = (c) => (c === FAV_CAT
                  ? Object.values(picked).filter((v) => (v.service?.party || "cargo_agency") === svcParty && v.service?.is_favorite).length
                  : (selByCat[c] || 0));
                return cats.map((c) => (
                  <button key={c} onClick={() => { setSvcCat(c); setSvcSearch(""); }} className={`flex items-center justify-between p-3 rounded-lg border text-left transition-colors ${c === FAV_CAT ? "border-amber-300 bg-amber-50/60 hover:border-amber-400" : "border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/40"}`} data-testid={`svc-cat-btn-${c}`}>
                    <div className="flex items-center gap-2">
                      {c === FAV_CAT && <Star size={16} className="text-amber-500 fill-amber-400" />}
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{c === FAV_CAT ? "Favourites" : c}</p>
                        <p className="text-xs text-slate-400">{countFor(c)} services</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {selFor(c) ? <span className="bg-emerald-600 text-white text-xs rounded-full px-2 py-0.5">{selFor(c)}</span> : null}
                      <ChevronRight size={16} className="text-slate-400" />
                    </div>
                  </button>
                ));
              })()}
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Button variant="ghost" size="sm" onClick={() => setSvcCat(null)} data-testid="svc-back-btn"><ChevronLeft size={16} className="mr-1" /> Back</Button>
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input placeholder="Search in this category…" value={svcSearch} onChange={(e) => setSvcSearch(e.target.value)} className="pl-9" data-testid="svc-picker-search" />
                </div>
              </div>
              <div className="border border-slate-200 rounded-lg max-h-[52vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-2 px-3">Pick</th>
                      <th className="py-2 px-3">Service</th>
                      <th className="py-2 px-3">Unit</th>
                      <th className="py-2 px-3">Qty</th>
                      <th className="py-2 px-3">Unit €</th>
                      <th className="py-2 px-3">Amount</th>
                    </tr>
                  </thead>
                  <tbody data-testid="svc-picker-list">
                    {allServices
                      .filter((s) => (s.party || "cargo_agency") === svcParty)
                      .filter((s) => (svcCat === FAV_CAT ? s.is_favorite : (s.category || "Other") === svcCat))
                      .filter((s) => !svcSearch || s.name.toLowerCase().includes(svcSearch.toLowerCase()))
                      .map((s) => {
                        const p = picked[s.id];
                        return (
                          <tr key={s.id} className="border-b border-slate-100">
                            <td className="py-2 px-3"><input type="checkbox" checked={!!p} onChange={() => toggleSvc(s, svcEntry)} className="w-4 h-4 accent-emerald-600" data-testid={`svc-pick-${s.id}`} /></td>
                            <td className="py-2 px-3"><span className="font-medium text-slate-800">{s.name}</span>{s.notes ? <span className="block text-xs text-slate-400">{s.notes}</span> : null}</td>
                            <td className="py-2 px-3 text-slate-500 whitespace-nowrap">{s.unit || "—"}</td>
                            <td className="py-2 px-3"><Input type="number" value={p ? p.qty : ""} disabled={!p} onChange={(e) => setSvcField(s.id, "qty", e.target.value)} className="h-8 w-20" data-testid={`svc-qty-${s.id}`} /></td>
                            <td className="py-2 px-3"><Input type="number" step="0.0001" value={p ? p.unit_price : (s.price === "" ? "" : s.price)} disabled={!p} onChange={(e) => setSvcField(s.id, "unit_price", e.target.value)} className="h-8 w-24" data-testid={`svc-price-${s.id}`} /></td>
                            <td className="py-2 px-3 font-medium whitespace-nowrap">{p ? `€ ${(Number(p.qty || 0) * Number(p.unit_price || 0)).toFixed(2)}` : "—"}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <DialogFooter className="items-center justify-between sm:justify-between">
            <span className="text-sm text-slate-600" data-testid="svc-total-split">
              <span className="text-blue-600 font-semibold">Airline € {svcTotalAirline.toFixed(2)}</span>
              <span className="text-slate-300 mx-2">·</span>
              <span className="text-emerald-600 font-semibold">Cargo Agency € {svcTotalAgency.toFixed(2)}</span>
              <span className="hidden sm:inline text-slate-400"> · <span data-testid="svc-total">Total € {svcTotal.toFixed(2)}</span> ({pickedArr.length})</span>
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSvcEntry(null)}>Cancel</Button>
              <Button onClick={saveSvc} disabled={savingSvc} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="svc-save-btn">{savingSvc ? "Saving…" : "Save Services"}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="cargo-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this cargo entry?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="cargo-confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {splitEntry && (
        <PlanningSplitDialog
          entry={splitEntry}
          flights={allFlights}
          existing={byEntry[splitEntry.id] || []}
          onClose={() => setSplitEntry(null)}
          onDone={() => { setSplitEntry(null); load(); }}
        />
      )}
    </div>
  );
}

/* ---------- Split an AWB across existing flights ---------- */
function PlanningSplitDialog({ entry, flights, existing, onClose, onDone }) {
  const totalP = Number(entry.pieces) || 0;
  const totalW = Number(entry.weight_kg) || 0;
  const initRows = existing.length > 0
    ? existing.map((a) => ({ flight_id: a.flight_id, pieces: a.pieces ?? "", weight_kg: a.weight_kg ?? "" }))
    : [{ flight_id: "", pieces: totalP || "", weight_kg: totalW || "" }, { flight_id: "", pieces: "", weight_kg: "" }];
  const [rows, setRows] = useState(initRows);
  const [saving, setSaving] = useState(false);

  const setRow = (i, k, v) => setRows((p) => p.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows((p) => [...p, { flight_id: "", pieces: "", weight_kg: "" }]);
  const delRow = (i) => setRows((p) => p.filter((_, j) => j !== i));

  const sumP = rows.reduce((s, r) => s + (Number(r.pieces) || 0), 0);
  const sumW = Math.round(rows.reduce((s, r) => s + (Number(r.weight_kg) || 0), 0) * 10) / 10;
  const pOk = sumP === totalP;
  const wOk = Math.abs(sumW - Math.round(totalW * 10) / 10) <= 0.5;
  const flightLabel = (f) => `${f.flight_number} · ${f.flight_date} · ${(f.origin || "?")}→${(f.destination || "?")}`;

  const save = async () => {
    const allocations = rows
      .filter((r) => r.flight_id && Number(r.pieces) > 0 && Number(r.weight_kg) > 0)
      .map((r) => ({ flight_id: r.flight_id, pieces: Number(r.pieces), weight_kg: Number(r.weight_kg) }));
    if (allocations.length < 1) { toast.error("Add at least one flight with pieces and weight"); return; }
    const ids = allocations.map((a) => a.flight_id);
    if (new Set(ids).size !== ids.length) { toast.error("Pick a different flight for each part"); return; }
    if (!pOk || !wOk) { toast.error(`Parts must total ${totalP} pcs / ${totalW} kg`); return; }
    setSaving(true);
    try {
      await splitEntryAcrossFlights(entry.id, allocations);
      toast.success(allocations.length > 1 ? "AWB split across flights" : "AWB assigned to flight");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to split"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Split AWB {entry.shipment_id || ""} across flights</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600 -mt-1">
          Total to allocate: <span className="font-semibold text-slate-900">{totalP} pcs</span> · <span className="font-semibold text-slate-900">{totalW} kg</span>. Add a line per flight — the parts must add up exactly.
        </p>
        <div className="space-y-2 max-h-[45vh] overflow-y-auto">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`split-row-${i}`}>
              <select value={r.flight_id} onChange={(e) => setRow(i, "flight_id", e.target.value)} className="flex-1 h-10 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid={`split-flight-${i}`}>
                <option value="">Choose flight…</option>
                {flights.map((f) => <option key={f.id} value={f.id}>{flightLabel(f)}</option>)}
              </select>
              <Input type="number" value={r.pieces} onChange={(e) => setRow(i, "pieces", e.target.value)} placeholder="pcs" className="w-24" data-testid={`split-pieces-${i}`} />
              <Input type="number" value={r.weight_kg} onChange={(e) => setRow(i, "weight_kg", e.target.value)} placeholder="kg" className="w-28" data-testid={`split-weight-${i}`} />
              <Button size="icon" variant="ghost" onClick={() => delRow(i)} disabled={rows.length <= 1} className="h-9 w-9 text-slate-400 hover:text-red-600" data-testid={`split-del-${i}`}><Trash2 size={16} /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addRow} data-testid="split-add-row"><Plus size={14} className="mr-1" /> Add flight</Button>
        </div>
        <div className="flex items-center gap-4 text-sm mt-1" data-testid="split-sums">
          <span className={pOk ? "text-emerald-700 font-medium" : "text-red-600 font-medium"}>Pieces: {sumP} / {totalP}</span>
          <span className={wOk ? "text-emerald-700 font-medium" : "text-red-600 font-medium"}>Weight: {sumW} / {totalW} kg</span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="split-cancel">Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} disabled={saving || !pOk || !wOk} data-testid="split-save">
            {saving ? "Saving…" : "Save split"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
